import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database, Download, Trash2, Plus, Clock, FileArchive,
} from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';

interface BackupRow {
  id: number;
  filename: string;
  file_size?: number;
  table_count?: number;
  status?: string;
  created_at: string;
  download_count?: number;
  notes?: string;
}

function fmtSize(b?: number) {
  if (!b) return '—';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
}

export function BackupPage() {
  const t = useT();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['backups'],
    queryFn: async () => {
      const r = await api<{ data?: { backups?: BackupRow[] } }>('admin/backup.php');
      return r.data?.backups ?? [];
    },
  });

  const create = useMutation({
    mutationFn: () => api('admin/backup.php', { method: 'POST', json: { action: 'create' } }),
    onSuccess: () => {
      toast.success(t('backup.success_create'));
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(t('backup.fail_create'), e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api(`admin/backup.php?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('backup.success_delete'));
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(t('error.delete_failed'), e.message),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('backup.title')}
        description={t('backup.desc')}
        actions={
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            loading={create.isPending}
            onClick={async () => {
              const ok = await confirmDialog({
                title: t('backup.create_confirm'),
                description: t('backup.create_desc'),
                confirmLabel: t('backup.start'),
              });
              if (ok) create.mutate();
            }}
          >
            {t('backup.create')}
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="text-left font-semibold px-5 py-3">{t('backup.col.file')}</th>
                <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">{t('backup.col.size')}</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">{t('backup.col.tables')}</th>
                <th className="text-left font-semibold px-5 py-3">{t('backup.col.created')}</th>
                <th className="text-right font-semibold px-5 py-3">{t('backup.col.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}><td colSpan={5} className="px-5 py-3"><Skeleton className="h-4 w-full" /></td></tr>
                ))
              ) : (list.data ?? []).length === 0 ? (
                <tr><td colSpan={5}>
                  <EmptyState
                    icon={<Database className="w-7 h-7" />}
                    title={t('backup.empty')}
                    description={t('backup.empty_desc')}
                    action={{
                      label: t('backup.create_now'),
                      onClick: () => create.mutate(),
                    }}
                  />
                </td></tr>
              ) : (
                (list.data ?? []).map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50/70">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <FileArchive className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-mono text-xs text-slate-900">{b.filename}</div>
                          {b.notes && <div className="text-xs text-slate-500 mt-0.5">{b.notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-slate-600">{fmtSize(b.file_size)}</td>
                    <td className="px-5 py-3 hidden lg:table-cell">
                      {b.table_count ? <Badge tone="info">{b.table_count} {t('backup.table_unit')}</Badge> : '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      <Clock className="inline w-3 h-3 mr-1" />
                      {new Date(b.created_at).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('btn.download')}
                          onClick={() => {
                            window.location.href = `../api/admin/backup.php?action=download&id=${b.id}`;
                          }}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('btn.delete')}
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: `${t('btn.delete')} backup "${b.filename}"?`,
                              description: t('backup.delete_confirm'),
                              destructive: true,
                              confirmLabel: t('btn.delete'),
                            });
                            if (ok) remove.mutate(b.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
